import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { Copy, Plus, User, Phone, Search, Send, Upload, ArrowLeft, CheckCircle2, Pencil, Trash2, AlertTriangle, Save } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../lib/api';
import { uploadFile } from '../../lib/upload';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';

function uniqueCustomers(rows = []) {
  const seen = new Set();
  return rows.filter((customer) => {
    if (customer.status === 'inactive') return false;
    const key = customer.user_id || customer.phone || customer.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const emptyForm = { full_name: '', phone: '', user_id: '', email: '', address: '', area: '', delivery_notes: '', opening_balance: 0 };
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [customerAction, setCustomerAction] = useState(null);
  const [lookupPhone, setLookupPhone] = useState('');
  const [lookupState, setLookupState] = useState({ step: 'phone', checking: false, result: null });

  const load = () => apiGet('/merchant/customers').then(r => setList(uniqueCustomers(r.data || []))).catch(e => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, opening_balance: Number(form.opening_balance || 0) };
      if (!payload.user_id) delete payload.user_id;
      await apiPost('/merchant/customers', payload);
      toast.success('Customer added');
      closeAdd();
      load();
    } catch (err) { toast.error(err.message); }
  };

  const openAdd = () => {
    setLookupPhone('');
    setForm(emptyForm);
    setLookupState({ step: 'phone', checking: false, result: null });
    setShowAdd(true);
  };

  const closeAdd = () => {
    setShowAdd(false);
    setLookupPhone('');
    setForm(emptyForm);
    setLookupState({ step: 'phone', checking: false, result: null });
  };

  const checkPhone = async (e) => {
    e.preventDefault();
    const phone = lookupPhone.trim();
    if (!phone) {
      toast.error('Enter mobile number');
      return;
    }
    setLookupState({ step: 'phone', checking: true, result: null });
    try {
      const res = await apiGet(`/merchant/customers/lookup-by-phone?phone=${encodeURIComponent(phone)}`);
      const result = res.data || {};
      if (result.already_customer) {
        setLookupState({ step: 'existing', checking: false, result });
        return;
      }
      const customer = result.customer || {};
      setForm({
        ...emptyForm,
        full_name: customer.full_name || '',
        phone: customer.phone || result.phone || phone,
        user_id: customer.user_id || '',
        email: customer.email || '',
      });
      setLookupState({ step: 'details', checking: false, result });
      if (result.found) toast.success('Customer profile found. Review and add details.');
    } catch (err) {
      setLookupState({ step: 'phone', checking: false, result: null });
      toast.error(err.message);
    }
  };

  const openEdit = (customer) => {
    setEditing(customer);
    setEditForm({
      ...emptyForm,
      full_name: customer.full_name || '',
      phone: customer.phone || '',
      user_id: customer.user_id || '',
      email: customer.email || '',
      address: customer.address || '',
      area: customer.area || '',
      delivery_notes: customer.delivery_notes || '',
      opening_balance: customer.opening_balance || 0,
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const payload = { ...editForm, opening_balance: Number(editForm.opening_balance || 0) };
      if (!payload.user_id) delete payload.user_id;
      await apiPatch(`/merchant/customers/${editing.id}`, payload);
      toast.success('Customer updated');
      setEditing(null);
      setEditForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async () => {
    if (!customerAction) return;
    try {
      const hardDelete = customerAction.mode === 'delete';
      await apiDelete(`/merchant/customers/${customerAction.customer.id}${hardDelete ? '?hard=true' : ''}`);
      toast.success(hardDelete ? 'Customer deleted' : 'Customer deactivated');
      setList((rows) => rows.filter((row) => row.id !== customerAction.customer.id));
      setCustomerAction(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const invite = async (customer) => {
    try {
      const res = await apiGet(`/merchant/customers/${customer.id}/invite-link`);
      const origin = window.location.origin;
      const url = `${origin}${res.data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const shareInvite = async (customer) => {
    try {
      const res = await apiGet(`/merchant/customers/${customer.id}/invite-link`);
      const url = `${window.location.origin}${res.data.path}`;
      const message = `Link your RozOps customer account: ${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filtered = list.filter(c => !q || (c.full_name || '').toLowerCase().includes(q.toLowerCase()) || (c.phone || '').includes(q));

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Customers</h1>
        <div className="flex gap-2">
          <ImportButton onDone={load} />
          <button onClick={openAdd} data-testid="add-customer-btn" className="bg-rozops-blue hover:bg-rozops-blue-dark text-white rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add customer</button>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rozops-text-muted" />
        <input placeholder="Search by name or phone" className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-hidden">
        <table className="w-full">
          <thead className="bg-rozops-bg border-b border-gray-100">
            <tr className="text-left text-xs uppercase tracking-wider text-rozops-text-muted font-bold">
              <th className="py-3 px-6">Name</th><th className="py-3 px-6">Phone</th><th className="py-3 px-6">Area</th><th className="py-3 px-6">Balance</th><th className="py-3 px-6"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-rozops-text-muted">No customers yet. Add your first customer to get started.</td></tr>}
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-gray-50 hover:bg-rozops-bg/50 transition-colors" data-testid={`customer-row-${c.id}`}>
                <td className="py-4 px-6"><div className="flex items-center gap-2"><User className="w-4 h-4 text-rozops-blue" /> <span className="font-semibold text-rozops-text">{c.full_name}</span></div></td>
                <td className="py-4 px-6 text-sm text-rozops-text-muted flex items-center gap-1.5 pt-6"><Phone className="w-3.5 h-3.5" />{c.phone || '—'}</td>
                <td className="py-4 px-6 text-sm text-rozops-text-muted">{c.area || '—'}</td>
                <td className="py-4 px-6 text-sm font-semibold text-rozops-text">₹{Number(c.opening_balance || 0).toFixed(2)}</td>
                <td className="py-4 px-6 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => invite(c)} title="Copy invite link" className="text-rozops-blue text-xs font-semibold inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Invite</button>
                    <button onClick={() => shareInvite(c)} title="Share invite" className="text-rozops-green text-xs font-semibold inline-flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Share</button>
                    <button onClick={() => openEdit(c)} className="text-rozops-text-muted hover:text-rozops-blue text-xs font-semibold inline-flex items-center gap-1" data-testid={`edit-customer-${c.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                    <button onClick={() => setCustomerAction({ mode: 'deactivate', customer: c })} className="text-amber-600 text-xs font-semibold inline-flex items-center gap-1" data-testid={`deactivate-customer-${c.id}`}><AlertTriangle className="w-3.5 h-3.5" /> Deactivate</button>
                    <button onClick={() => setCustomerAction({ mode: 'delete', customer: c })} className="text-red-500 text-xs font-semibold inline-flex items-center gap-1" data-testid={`delete-customer-${c.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={closeAdd}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-xl text-rozops-navy mb-4">Add customer</h2>
            {lookupState.step === 'phone' && (
              <form onSubmit={checkPhone} className="space-y-3">
                <p className="text-sm text-rozops-text-muted">Enter mobile number first. RozOps will check if this customer profile already exists.</p>
                <div>
                  <label className="block text-sm font-semibold text-rozops-text mb-1.5">Mobile number</label>
                  <input autoFocus required placeholder="Customer mobile number" className={inputCls} value={lookupPhone} onChange={(e) => setLookupPhone(e.target.value)} data-testid="cust-phone-lookup" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={closeAdd} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                  <button type="submit" disabled={lookupState.checking} data-testid="cust-check-phone" className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold disabled:opacity-60">
                    {lookupState.checking ? 'Checking...' : 'Continue'}
                  </button>
                </div>
              </form>
            )}

            {lookupState.step === 'existing' && (
              <div className="space-y-4">
                <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
                  <div className="font-heading font-bold text-amber-800">Customer already exists</div>
                  <div className="mt-1 text-sm text-amber-700">
                    {lookupState.result?.customer?.full_name || 'This customer'} is already added to your customer list.
                  </div>
                  <div className="mt-3 text-sm text-amber-800">
                    {lookupState.result?.customer?.phone}
                    {lookupState.result?.customer?.area ? ` · ${lookupState.result.customer.area}` : ''}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setLookupState({ step: 'phone', checking: false, result: null })} className="px-4 py-2 rounded-xl text-rozops-text-muted inline-flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" /> Try another
                  </button>
                  <button type="button" onClick={closeAdd} className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold">Done</button>
                </div>
              </div>
            )}

            {lookupState.step === 'details' && (
              <form onSubmit={add} className="space-y-3">
                {lookupState.result?.found ? (
                  <div className="rounded-2xl bg-rozops-green-light border border-green-100 p-4 text-sm text-rozops-green flex gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Existing customer profile found. Confirm the details and add delivery information.</span>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-rozops-blue-light border border-blue-100 p-4 text-sm text-rozops-blue">No profile found. Add customer details below.</div>
                )}
                <input required placeholder="Full name" className={inputCls} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="cust-name" />
                <input required placeholder="Phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" />
                <input type="email" placeholder="Email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" />
                <input placeholder="Address" className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <input placeholder="Area" className={inputCls} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
                <input type="number" step="0.01" placeholder="Opening balance" className={inputCls} value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
                <input placeholder="Delivery notes" className={inputCls} value={form.delivery_notes} onChange={(e) => setForm({ ...form, delivery_notes: e.target.value })} />
                <div className="flex justify-between gap-2 pt-2">
                  <button type="button" onClick={() => setLookupState({ step: 'phone', checking: false, result: null })} className="px-4 py-2 rounded-xl text-rozops-text-muted inline-flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <div className="flex gap-2">
                    <button type="button" onClick={closeAdd} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                    <button type="submit" data-testid="cust-submit" className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold">Add</button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-xl text-rozops-navy mb-4">Edit customer</h2>
            <form onSubmit={saveEdit} className="space-y-3">
              <input required placeholder="Full name" className={inputCls} value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="edit-cust-name" />
              <input required placeholder="Phone" className={inputCls} value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-cust-phone" />
              <input type="email" placeholder="Email" className={inputCls} value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-cust-email" />
              <input placeholder="Address" className={inputCls} value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="edit-cust-address" />
              <input placeholder="Area" className={inputCls} value={editForm.area} onChange={(e) => setEditForm({ ...editForm, area: e.target.value })} data-testid="edit-cust-area" />
              <input type="number" step="0.01" placeholder="Opening balance" className={inputCls} value={editForm.opening_balance} onChange={(e) => setEditForm({ ...editForm, opening_balance: e.target.value })} data-testid="edit-cust-opening-balance" />
              <input placeholder="Delivery notes" className={inputCls} value={editForm.delivery_notes} onChange={(e) => setEditForm({ ...editForm, delivery_notes: e.target.value })} data-testid="edit-cust-notes" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                <button type="submit" data-testid="edit-cust-submit" className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {customerAction && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-4 z-50" onClick={() => setCustomerAction(null)}>
          <div className="bg-white rounded-3xl shadow-medium p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="delete-customer-title">
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-gray-200 sm:hidden" />
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 grid place-items-center mb-4">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h2 id="delete-customer-title" className="font-heading font-bold text-xl text-rozops-navy">
              {customerAction.mode === 'delete' ? 'Delete customer?' : 'Deactivate customer?'}
            </h2>
            <p className="mt-1 text-sm text-rozops-text-muted">
              {customerAction.mode === 'delete'
                ? `${customerAction.customer.full_name} and related customer records will be permanently deleted.`
                : `${customerAction.customer.full_name} will be hidden from active customer workflows, but history remains available in the database.`}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setCustomerAction(null)} className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-rozops-text">Cancel</button>
              <button type="button" onClick={remove} className="rounded-xl bg-red-500 px-4 py-3 text-sm font-semibold text-white" data-testid="confirm-delete-customer">
                {customerAction.mode === 'delete' ? 'Delete' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportButton({ onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const res = await uploadFile('/merchant/customers/import', f);
      const d = res?.data || {};
      toast.success(`Imported ${d.inserted || 0} customers (skipped ${d.skipped || 0})`);
      onDone && onDone();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-file-input" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        data-testid="import-csv-btn"
        className="bg-white border-2 border-rozops-blue-light hover:border-rozops-blue text-rozops-text rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2 disabled:opacity-50">
        <Upload className="w-4 h-4" /> {busy ? 'Importing…' : 'Import CSV'}
      </button>
    </>
  );
}
